import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Modal, notification, Select } from 'antd'
import * as Icons from '@ant-design/icons'

import { useApiQuery } from 'admin/lib/Api'
import {
  useRecordModuleTransferer,
  useRecordProjectTransferer,
  useProject
} from 'admin/lib/Api/queries'

const supportedModules = [
  { label: 'Deliveries', key: 'Delivery' },
  { label: 'Waste', key: 'Waste' }
]

export function TransferItem({ recordStatus, type, ...props }) {
  const { accountId, projectId, moduleName = '' } = useParams()
  const params = useParams()

  const project = useProject(accountId, projectId)
  const relatedProjects = useApiQuery(
    useCallback(
      api => {
        return api.listProjects(accountId)
      },
      [accountId]
    )
  )

  const moduleTransferer = useRecordModuleTransferer()
  const projectTransferer = useRecordProjectTransferer()

  const recordTransferable =
    recordStatus !== 'Published' && recordStatus !== 'AutoPublished'

  const RECORD_NOT_TRANSFERABLE_MESSAGE =
    'You cannot transfer records that are in a Published or AutoPublished state. If you need to tranfer this record, change the state and Save.'

  const handleModuleTransfer = useCallback(
    async ({ label, key }) => {
      const doTransfer = await getUserConfirmationChangeModule(label)
      if (!doTransfer) {
        return
      }

      const closeProgressIndicator = openTransferProgressIndicator(label)
      try {
        const newRecord = await moduleTransferer.mutateAsync({
          ...params,
          destinationModuleName: key
        })

        notification.success({
          message: `Transferred successfully. This window will now close`,
          duration: 6,
          onClose: () => {
            window.opener = null
            window.open('', '_self')
            // close window
            window.close()
          }
        })
        console.log('newRecord', newRecord.recordId)
      } catch (ex) {
        let message = String(ex.response?.data ?? ex)

        if (message === 'record_void') {
          message =
            'The Record is Void and can not be transferred, as it may have already been transferred. To continue please set the record to another status.'
        }

        notification.error({
          message: `🤒 Problem transferring record: ${message}`,
          duration: 0
        })
      } finally {
        closeProgressIndicator()
      }
    },
    [params, moduleTransferer]
  )

  const handleProjectTransfer = useCallback(async () => {
    const projects = relatedProjects.data.map(project => {
      return {
        value: project.projectId,
        label: project.name
      }
    })
    const destinationProjectId = await getUserConfirmationChangeProject(
      accountId,
      projectId,
      projects
    )
    if (!destinationProjectId) {
      return
    }

    const label = projects.find(
      project => project.value === destinationProjectId
    ).label

    const closeProgressIndicator = openTransferProgressIndicator(label)

    try {
      const newRecord = await projectTransferer.mutateAsync({
        ...params,
        destinationProjectId: destinationProjectId
      })

      notification.success({
        message: `Transferred successfully.  This window will now close`,
        duration: 6,
        onClose: () => {
          window.opener = null
          window.open('', '_self')
          // close window
          window.close()
        }
      })
      console.log('newRecord', newRecord.recordId)
    } catch (ex) {
      let message = String(ex.response?.data ?? ex)

      if (message === 'record_void') {
        message =
          'The Record is Void and can not be transferred, as it may have already been transferred. To continue please set the record to another status.'
      }

      notification.error({
        message: `🤒 Problem transferring record: ${message}`,
        duration: 0
      })
    } finally {
      closeProgressIndicator()
    }
  }, [accountId, params, projectId, projectTransferer, relatedProjects.data])

  if (
    supportedModules.every(
      m => m.key.toLowerCase() !== moduleName.toLowerCase()
    )
  ) {
    // Only display on supported modules
    return null
  }

  if (type === 'module') {
    return (
      <>
        {supportedModules
          .filter(m => m.key.toLowerCase() !== moduleName.toLowerCase())
          .map(m => {
            const enabled =
              project.isFetched &&
              recordTransferable &&
              project.data?.moduleTags?.find(
                mt => mt.moduleTagDefinitionName === m.key
              )?.isTagged

            return (
              <div
                key={m.key}
                onClick={() => handleModuleTransfer(m)}
                disabled={!enabled}
                title={
                  !recordTransferable ? RECORD_NOT_TRANSFERABLE_MESSAGE : null
                }
              >
                Transfer to {m.label} module
              </div>
            )
          })}
      </>
    )
  } else {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {relatedProjects &&
          relatedProjects.data &&
          relatedProjects.data.length > 1 && (
            <div
              onClick={handleProjectTransfer}
              disabled={!project.isFetched || !recordTransferable}
              title={
                !recordTransferable ? RECORD_NOT_TRANSFERABLE_MESSAGE : null
              }
            >
              Transfer to Another Project
            </div>
          )}
      </>
    )
  }
}

const getUserConfirmationChangeModule = async label => {
  return new Promise((resolve, reject) => {
    Modal.confirm({
      title: `Transfer to ${label}?`,
      content: (
        <div>
          <span>Transferring this record will:</span>
          <ul>
            <li>
              <strong>Void</strong> the original
            </li>
            <li>
              Transfer <strong>minimal</strong> field data
            </li>
            <li>Close this window</li>
          </ul>
          <span>Continue?</span>
        </div>
      ),
      okText: 'Transfer 💪',
      cancelText: 'Nevermind... 🙉',
      onOk: () => resolve(true),
      onCancel: () => resolve(false)
    })
  })
}

const getUserConfirmationChangeProject = async (
  accountId,
  projectId,
  projects
) => {
  const currentProject = projects.find(project => project.value === projectId)

  // Remove current project from list of projects
  const filteredProjects = projects.filter(
    project => project.value !== projectId
  )

  let selectedValue = filteredProjects[0].value
  const handleSelectChange = value => (selectedValue = value)

  return new Promise((resolve, reject) => {
    Modal.confirm({
      title: 'Transfer record to...',
      content: (
        <>
          <p>
            Current Project:
            <br />
            <strong>{currentProject.label}</strong>
          </p>
          <div style={{ margin: '0.5rem 0' }}>
            New Project:
            <br />
            <Select
              showSearch
              disabled={filteredProjects.length < 1}
              options={filteredProjects}
              defaultValue={filteredProjects[0].value}
              placeholder="Select project"
              style={{ width: '100%' }}
              onChange={handleSelectChange}
            />
          </div>
          <div>
            <span>Transferring this record will:</span>
            <ul>
              <li>
                <strong>Void</strong> the original
              </li>
              <li>
                Transfer <strong>minimal</strong> field data
              </li>
              <li>Close this window</li>
            </ul>
            <span>Continue?</span>
          </div>
        </>
      ),
      okText: 'Transfer 💪',
      cancelText: 'Nevermind... 🙉',
      onOk: () => resolve(selectedValue),
      onCancel: () => resolve(false)
    })
  })
}

const openTransferProgressIndicator = label => {
  const inProgressKey = 'TransferInProgressNotification'

  notification.info({
    icon: <Icons.LoadingOutlined />,
    message: `Transferring to ${label}`,
    key: inProgressKey
  })

  return () => {
    notification.destroy(inProgressKey)
  }
}
